import React, { useEffect, useState } from "react";
import TableHeader from "../../common/MagicPageHeader";
import Header from "../../common/MagicTableHeader";
import MagicTable from "../../common/MagicTable";
import DownloadToXlsxFile from "../../../helper/DownloadToXlsxFile";
import ImageComponent from "../../mui/Image";
import { useFetchSessionQualityReportsQuery } from "../../../store/apis/reportsApis";

interface TabCategory {
  id: number;
  name: string;
  code: string;
}

interface SessionQualityTableProps {
  selectedTab: TabCategory | null;
  magicPageHeaderBtn: {
    text: string;
    variant: string;
    startIcon: React.ReactNode;
    onClick: () => void;
    sx: Record<string, string>;
  }[];
  magicTableHeaderBtn: any[];
  params:any;
  isInvalidSearch: boolean;
  onPaginationChange: (pageNo: number) => void;
  onSearch: (searchTerm: string) => void;
}

const SessionQualityTable: React.FC<SessionQualityTableProps> = ({
  selectedTab,
  magicTableHeaderBtn,
  params,
  isInvalidSearch,
  onPaginationChange,
  onSearch
}) => {
  const [downloadParams, setDownloadParams] = useState<any>(null);
  const [downloadTrigger, setDownloadTrigger] = useState(false);
  const columns = [
    {
      field: "centreName",
      headerName: "Centre name",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "region",
      headerName: "Region",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "funder",
      headerName: "Funder",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "centreBusinessType",
      headerName: "Centre business type",
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "district",
      headerName: "City",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "cityManager",
      headerName: "City Manager",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "tcName",
      headerName: "TC name",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "tcJoiningDate",
      headerName: "TC joining date",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "dateOfObservation",
      headerName: "Date of Observation",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
        field: "tcEmplId",
        headerName: "TC Empl ID",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "knowledge",
        headerName: "Knowledge",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "skills",
        headerName: "Skills",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "attitude",
        headerName: "Attitude",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "assessorName",
        headerName: "Assessor Name",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
      {
        field: "remarks",
        headerName: "Remarks",
        flex: 1,
        minWidth: 150,
        sortable: true,
      },
  ];

  const {
    data: fetchedReports = {
      reports: [],
      totalReports: 0,
    },
    isLoading,
  } = useFetchSessionQualityReportsQuery(params);
  const { data: downloadData, isLoading: downloadLoading } =
    useFetchSessionQualityReportsQuery(downloadParams, {
      skip: !downloadParams,
    });
  const handleDownload = () => {
    const downloadPayload = {
      ...params,
      pageNo: "1",
      pageSize: fetchedReports?.totalReports || "10",
    };
    setDownloadParams(downloadPayload);
    setDownloadTrigger(true);
  };
  useEffect(() => {
    if (downloadData && downloadTrigger) {
      const sheetData = [
        [
          "Centre Name",
          "Region",
          "Funder",
          "Centre Business Type",
          "State",
          "City",
          "City Manager",
          "Status",
          "TC Name",
          "TC Joining Date",
          "Date of Observation",
          "TC Empl ID",
          "Knowledge",
          "Skills",
          "Attitude",
          "Assessor Name",
          "Remarks",
        ],
        ...(downloadData?.reports.map((report: any) => [
          report.centreName || "-",
          report.region || "-",
          report.funder || "-",
          report.centreBusinessType || "-",
          report.state || "-",
          report.district || "-",
          report.cityManager || "-",
          report.status || "-",
          report.tcName || "-",
          report.tcJoiningDate || "-",
          report.dateOfObservation || "-",
          report.tcEmplId || "-",
          report.knowledge || "-",
          report.skills || "-",
          report.attitude || "-",
          report.assessorName || "-",
          report.remarks || "-",
        ]) || []),
      ];
      DownloadToXlsxFile({
        fileNamePrifix: "Session-Quality-Report",
        sheetData,
      });
      setDownloadTrigger(false);  
    }
    }, [downloadData, downloadTrigger]);

  const magicPageHeaderBtn = [
    {
      text: "Download",
      variant: "outlined",
      startIcon: (
        <ImageComponent
          src="/assets/images/login/exportIcon.svg"
          alt="Export Icon"
        />
      ),
      onClick: handleDownload,
      loading: downloadLoading,
      sx: {
        background: "white",
        border: "1px solid #D0D5DD",
        borderRadius: "8px",
        color: "black",
      },
    },
  ];
  const totalPages = Math.max(1, Math.ceil((fetchedReports?.totalReports || 0) /10));
  return (
    <>
      <TableHeader
        title={`${selectedTab?.name || "Session Quality"} Report`}
        subTitle="Here you can view the report with all the details."
        buttons={magicPageHeaderBtn}
        chipLabel={`${fetchedReports?.totalReports || 0} entries`}
      />
      <Header
        buttons={magicTableHeaderBtn}
        onSearchfieldChange={(value) => onSearch(String(value))}
        isInvalidSearch={isInvalidSearch}
      />
      <MagicTable
        columns={columns}
        rows={fetchedReports?.reports}
        loading={isLoading}
        handlePageChange={(event, pageNo) => onPaginationChange(pageNo)}
        currentPage={parseInt(params?.pageNo) || 1}
        count={totalPages}
        rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
        headerSx={{
          paddingLeft: "16px",
          textAlign: "start",
          fontWeight: 600,
          color: "#242424",
          padding: "13px 16px",
        }}
        tableSx={{ minHeight: "300px", width: "100%" }}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#DADADA",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 550,
            fontSize: "14px",
          },
          "& .MuiDataGrid-row": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FFFFFF",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },}}
      />
    </>
  );
};

export default SessionQualityTable;